import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, TS_Checkbox, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { FHIRResourceParamType_Dynamic } from '@app/fhir-shared';
import { Component_EditorHeader } from '@app/styles';
import { SearchableItem_FHIRQuery } from '../consts.js';
import { __stringify, dbObjectToId, MimeType_json, sortArray } from '@nu-art/ts-common';
import './Editor_FHIRQuery.scss';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText } from '@app/styles/frontend/ui-manager/ui-components';
import { ModuleFE_FHIRResourceParam, ModuleFE_FHIRResourceParamValue, ModuleFE_FHIRResourceSubtype } from '../../../_entity.js';
import { DropDown_FHIRResourceSubtype } from '../../../_entity/fhir-resource-subtype/ui-components.js';
import { DropDown_FHIRResourceParamValue, MultiSelect_FHIRResourceParamValue } from '../../../_entity/fhir-resource-param-value/ui-components.js';
import { ICONS } from '@app/styles';
const viewMode_View = 'view';
const viewMode_Edit = 'edit';
export class Editor_FHIRQuery extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        state.editMode = nextProps.editMode ?? (this.state?.editMode ?? false);
        return state;
    }
    // ######################## Logic ########################
    getItemActions = () => {
        return [{
                label: 'Edit Query',
                action: async () => this.setState({ editMode: true }),
            }, {
                label: 'Delete Query',
                action: async () => {
                    await this.state.editable.delete();
                    this.props.deselectQuery?.();
                }
            }, {
                label: 'Export JSON',
                action: async () => {
                    const item = this.state.editable.item;
                    ModuleFE_Thunderstorm.downloadFile({
                        fileName: `FHIR Resource - ${item.label}`,
                        mimeType: MimeType_json,
                        content: __stringify(item, true)
                    });
                }
            }];
    };
    getEditModeActions = () => {
        const actions = [];
        //Actions for existing item
        if (this.state.editable.item._id)
            actions.push({
                label: 'Done',
                action: async () => this.setState({ editMode: false }),
            });
        //Actions for new item
        else {
            if (this.props.deselectQuery)
                actions.push({
                    label: 'Cancel',
                    action: async () => {
                        return this.props.deselectQuery();
                    },
                });
            actions.push({
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                },
            });
        }
        return actions;
    };
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    getParams = (optional) => {
        return sortArray(ModuleFE_FHIRResourceParam.cache.filter(param => {
            return param.resourceSubtypeId === this.state.editable.item.subtypeId
                && param.isOptional === optional;
        }), param => param.key);
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__fhir-query', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(Component_EditorHeader, { editable: this.state.editable, searchItem: SearchableItem_FHIRQuery, editMode: this.state.editMode, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions() });
    };
    renderBody = () => {
        return _jsxs(LL_V_L, { className: 'editor__fhir-query__body', children: [this.renderEditor_Label(), this.renderEditor_ResourceSubtype(), this.renderEditor_Params()] });
    };
    // ######################## Render - Editors ########################
    renderEditor_Label = () => {
        const query = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-query__body__value', children: query.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: query, prop: 'label' }) }
                ] }) });
    };
    renderEditor_ResourceSubtype = () => {
        const query = this.state.editable;
        const selectedSubtype = ModuleFE_FHIRResourceSubtype.cache.unique(query.item.subtypeId);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Resource Sub Type', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-query__body__value', children: selectedSubtype?.label ?? 'N/A' }) },
                    {
                        key: viewMode_Edit, renderer: () => _jsx(DropDown_FHIRResourceSubtype.editable, { editable: query, prop: 'subtypeId' })
                    }
                ] }) });
    };
    renderEditor_Params = () => {
        return _jsxs(LL_H_C, { className: 'editor__fhir-query__params', children: [this.renderEditor_Params_Mandatory(), this.renderEditor_Params_Optional()] });
    };
    renderEditor_Params_Mandatory = () => {
        const editableParams = this.state.editable.editProp('params', {});
        const params = this.getParams(false);
        return _jsxs(TS_PropRenderer.Vertical, { label: 'Params (Mandatory)', className: 'big', children: [!params.length && _jsx("div", { className: 'editor__fhir-query__body__value', children: "No fixed mandatory params set for this subtype!" }), params.map(param => {
                    const label = `${param.key} (${param.type})`;
                    //Simple render for dynamic params
                    if (param.type === FHIRResourceParamType_Dynamic)
                        return _jsx(TS_PropRenderer.Horizontal, { label: label }, param._id);
                    //Render for fixed params
                    const selectedIds = editableParams.item[param._id];
                    const selectedValues = ModuleFE_FHIRResourceParamValue.cache.filter(val => !!selectedIds?.includes(val._id));
                    const error = !selectedValues.length ? 'Mandatory param missing a value' : undefined;
                    return _jsx(TS_PropRenderer.Horizontal, { label: label, error: error, children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                                {
                                    key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-query__body__value', children: selectedValues.map(val => val.label ?? val.value).join(', ') || 'N/A' })
                                },
                                {
                                    key: viewMode_Edit, renderer: () => {
                                        if (!param.isMultiValue)
                                            return this.renderEditor_ParamSelector_Single(editableParams, param);
                                        return this.renderEditor_ParamSelector_Multi(editableParams, param);
                                    }
                                }
                            ] }) }, param._id);
                })] });
    };
    renderEditor_Params_Optional = () => {
        const editableParams = this.state.editable.editProp('params', {});
        const editableDynamic = this.state.editable.editProp('optionalDynamicParams', []);
        const params = this.getParams(true);
        return _jsx(TS_PropRenderer.Vertical, { label: 'Params (Optional)', className: 'big', children: params.map(param => {
                const label = `${param.key} (${param.type})`;
                if (param.type === FHIRResourceParamType_Dynamic) {
                    const paramIndex = editableDynamic.item.indexOf(param._id);
                    if (paramIndex === -1 && !this.state.editMode)
                        return;
                    return _jsx(TS_PropRenderer.Horizontal, { label: label, children: _jsx(TS_Checkbox, { disabled: !this.state.editMode, icon: _jsx(ICONS.v.component, {}), checked: paramIndex !== -1, onCheck: () => {
                                if (paramIndex !== -1)
                                    return editableDynamic.removeArrayItem(paramIndex);
                                return editableDynamic.updateArrayAt(param._id);
                            } }) }, param._id);
                }
                const selectedIds = editableParams.item[param._id];
                const selectedValues = ModuleFE_FHIRResourceParamValue.cache.filter(val => !!selectedIds?.includes(val._id));
                if (!selectedValues.length && !this.state.editMode)
                    return;
                return _jsx(TS_PropRenderer.Horizontal, { label: label, children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                            {
                                key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-query__body__value', children: selectedValues.map(val => val.label ?? val.value).join(', ') })
                            },
                            {
                                key: viewMode_Edit, renderer: () => {
                                    if (!param.isMultiValue)
                                        return this.renderEditor_ParamSelector_Single(editableParams, param);
                                    return this.renderEditor_ParamSelector_Multi(editableParams, param);
                                }
                            }
                        ] }) }, param._id);
            }) });
    };
    renderEditor_ParamSelector_Single = (editable, param) => {
        const selectedId = editable.item[param._id]?.[0];
        return _jsx(DropDown_FHIRResourceParamValue.selectable, { placeholder: 'Select Value', canUnselect: true, queryFilter: val => val.paramId === param._id, selected: selectedId, noOptionsRenderer: () => 'No more values under this param. you can add more in the FHIR Resource editor!', onSelected: value => editable.updateObj({ [param._id]: (value ? [value._id] : undefined) }) });
    };
    renderEditor_ParamSelector_Multi = (editable, param) => {
        const relevantValueIds = ModuleFE_FHIRResourceParamValue.cache.filter(val => val.paramId === param._id).map(dbObjectToId);
        return _jsx(MultiSelect_FHIRResourceParamValue, { editable: editable, itemFilter: item => relevantValueIds.includes(item), prop: param._id, itemsDirection: 'vertical' });
    };
}
//# sourceMappingURL=Editor_FHIRQuery.js.map